// V4-FRONT-ANY-USAGE batch 4 2026-05-28
import { X, User, Lock, Loader2, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { useState, useRef, useCallback, useMemo, ReactNode, lazy, Suspense } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { WatchTab } from '../features/tabs/WatchTab';
import { PositioningTab } from '../features/tabs/PositioningTab';
import { AutomationsTab } from '../features/tabs/AutomationsTab';
import { SettingsTab } from '../features/tabs/SettingsTab';
import { AccountTab } from '../features/tabs/AccountTab';
import { AnalyticsTab } from '../features/tabs/AnalyticsTab';
import { EngagementTab } from '../features/tabs/EngagementTab';
// V4-CODESPLIT-DASHBOARD 2026-05-29 — lazy-load heavy tabs
const SpacesTab = lazy(() => import('../features/tabs/SpacesTab').then(m => ({ default: m.SpacesTab })));
const SourcesTab = lazy(() => import('../features/tabs/SourcesTab').then(m => ({ default: m.SourcesTab })));
const ChannelsTab = lazy(() => import('../features/tabs/ChannelsTab').then(m => ({ default: m.ChannelsTab })));
const NewsletterTab = lazy(() => import('../features/tabs/NewsletterTab').then(m => ({ default: m.NewsletterTab })));

// V4-CODESPLIT-DASHBOARD 2026-05-29 — fallback spinner for lazy tabs
const TabSuspenseFallback = () => (
  <div className="flex items-center justify-center p-8">
    <Loader2 className="w-5 h-5 animate-spin" />
  </div>
);
import type { Source, WatchTopic, CommunicationChannel, AutomationSettings, User as UserType, Space } from '../../types';
import type { Post } from '../../types/post';
import { getMenuItems, getSettingsItem } from '../../config/menuItems';
import { DEFAULT_LOGO_URL, SIDEBAR_ITEMS } from '../../utils/Constants';
import { filterNavItems } from '../../utils/navGating';
import { computeChannelAlertLevel, type ChannelAlertLevel } from '../../utils/channelAlerts';
import { Logger } from '../../utils/logger';
import { useOnboardingProgress } from '../../hooks/useOnboardingProgress';
import { useIsAgencyOwner } from '../../hooks/useIsAgencyOwner';
import { useUnrepliedCount } from '../../hooks/useUnrepliedCount';

// ── SidebarButton — Bouton d'icône avec tooltip via portail ──

interface SidebarButtonProps {
  icon: ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  className?: string;
  title?: string;
  /**
   * Bouton verrouillé (onboarding incomplet ou feature coming-soon).
   * Si vrai, le clic est ignoré et l'icône est grisée. Le `disabledTooltip` remplace
   * `label` dans le tooltip pour expliquer pourquoi à l'utilisateur.
   */
  disabled?: boolean;
  disabledTooltip?: string;
  /**
   * V4-ZERNIO-INBOX-BADGE 2026-05-28 — pastille numérique en haut-droite de l'icône.
   * Affichée si > 0. Ex : commentaires non répondus sur l'onglet Engagement.
   */
  badgeCount?: number;
  /**
   * V4-ZERNIO-COST-PURGE 2026-06-05 — pastille d'alerte (triangle) en bas-droite.
   * 'warning' (jaune) = ≥1 canal entre dans la fenêtre 7j avant déconnexion ;
   * 'danger' (rouge) = ≥1 canal déjà déconnecté. Null = rien.
   */
  alertLevel?: ChannelAlertLevel;
  alertTooltip?: string;
  testId?: string;
}

function SidebarButton({ icon, label, isActive, onClick, className = '', title, disabled = false, disabledTooltip, badgeCount = 0, alertLevel = null, alertTooltip, testId }: SidebarButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setTooltipPos(null);
  }, []);

  // Couleur de l'icône :
  // - Disabled → text-muted (très gris)
  // - Actif → primary (bleu/violet)
  // - Hover (non actif) → blanc (sur fond violet)
  // - Défaut → gris secondaire
  const iconColor = disabled
    ? 'var(--color-text-muted)'
    : isActive
    ? 'var(--color-primary)'
    : isHovered
    ? 'var(--color-text-light)'
    : 'var(--color-text-secondary)';

  // Background du bouton :
  // - Disabled → transparent (pas de hover effect)
  // - Actif → halo violet léger pour signaler l'item courant (fix backlog 3.4 19-05)
  // - Hover (non actif) → violet primary plein
  // - Défaut → transparent
  const bgColor = disabled
    ? 'transparent'
    : isActive
    ? 'var(--color-primary-light, rgba(124, 58, 237, 0.12))'
    : isHovered
    ? 'var(--color-primary)'
    : 'transparent';

  const tooltipText = disabled && disabledTooltip
    ? disabledTooltip
    : (!disabled && alertLevel && alertTooltip ? alertTooltip : label);

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => {
          if (disabled) return;
          onClick();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        // Fix backlog 1.5 (19-05) : a11y — aria-label pour les lecteurs d'écran.
        // `title` reste pour le tooltip natif au survol (backup au tooltip custom).
        title={title || label}
        data-testid={testId}
        aria-label={label}
        aria-pressed={isActive}
        aria-disabled={disabled}
        className={`flex items-center justify-center w-10 h-10 mb-3 rounded-full transition-all relative mx-auto ${className} ${disabled ? 'cursor-not-allowed opacity-35' : ''}`}
        style={{ backgroundColor: bgColor }}
      >
        <span
          className="flex items-center justify-center transition-colors"
          style={{ color: iconColor }}
        >
          {icon}
        </span>
        {/* UX backlog 8.3 + V3-2.3 (19-05) : badge cadenas plus visible quand
            l'item est verrouillé. Fond warning léger (jaune-orange) au lieu
            de background neutre pour attirer l'œil et clarifier l'état.
            Toujours visible sans hover (utile sur touch / mobile). */}
        {disabled && (
          <span
            className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full"
            style={{
              width: 16,
              height: 16,
              backgroundColor: 'var(--color-warning-light, rgba(245, 158, 11, 0.15))',
              border: '1px solid var(--color-warning)',
            }}
            aria-hidden="true"
          >
            <Lock style={{ width: 9, height: 9, color: 'var(--color-warning)' }} />
          </span>
        )}
        {/* V4-ZERNIO-INBOX-BADGE 2026-05-28 — pastille count en haut-droite */}
        {!disabled && badgeCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center rounded-full px-1 font-semibold"
            style={{
              minWidth: 16,
              height: 16,
              fontSize: 10,
              lineHeight: 1,
              backgroundColor: 'var(--color-danger, #ef4444)',
              color: '#fff',
              border: '1.5px solid var(--color-background)',
            }}
            aria-label={`${badgeCount} non lu${badgeCount > 1 ? 's' : ''}`}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
        {/* V4-ZERNIO-COST-PURGE 2026-06-05 — pastille d'alerte canal (haut-droite).
            Jaune = déconnexion proche (≤7j) ; rouge = canal déconnecté. */}
        {!disabled && alertLevel && (
          <span
            // V4-CHANNEL-WARNING-HARDEN 2026-06-15 — position en inline style
            // (Tailwind figé : `-right-1.5` serait un no-op). Décalée un peu plus
            // à droite (right: -7px vs -4px) pour mieux détacher la pastille de
            // l'icône, comme demandé.
            className="absolute flex items-center justify-center rounded-full"
            style={{
              top: -4,
              right: -7,
              width: 16,
              height: 16,
              backgroundColor: alertLevel === 'danger'
                ? 'var(--color-danger, #ef4444)'
                : 'var(--color-warning, #f59e0b)',
              border: '1.5px solid var(--color-background)',
            }}
            aria-label={alertTooltip || (alertLevel === 'danger' ? 'Canal déconnecté' : 'Canal bientôt déconnecté')}
          >
            <AlertTriangle style={{ width: 9, height: 9, color: '#fff' }} />
          </span>
        )}
      </button>

      {/* Tooltip rendu dans document.body via portal pour échapper à tout stacking context */}
      {tooltipPos && createPortal(
        <div
          className="px-3 py-1.5 rounded-md text-sm pointer-events-none"
          style={{
            position: 'fixed',
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translateY(-50%)',
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-text-light)',
            zIndex: 9999,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            maxWidth: disabled ? 280 : undefined,
            whiteSpace: disabled ? 'normal' : 'nowrap',
          }}
        >
          {tooltipText}
        </div>,
        document.body
      )}
    </>
  );
}

// ── Main Sidebar component ──

interface SidebarProps {
  expanded: boolean;
  activeTab?: string | null;
  onTabChange?: (tabId: string | null) => void;
  onToggle: () => void;
  sources: Source[];
  onAddSource: (source: Omit<Source, 'id'>) => void;
  onUpdateSource: (id: string, source: Omit<Source, 'id'>) => void;
  onDeleteSource: (id: string) => void;
  onRefreshSources?: () => Promise<void>;
  watchTopics: WatchTopic[];
  enrichingTopicIds?: Set<string>;
  onAddTopic: (topic: Omit<WatchTopic, 'id'>) => void;
  onUpdateTopic: (id: string, topic: Omit<WatchTopic, 'id'>) => void;
  onDeleteTopic: (id: string) => void;
  communicationChannels: CommunicationChannel[];
  onAddCommunicationChannel: (channel: Omit<CommunicationChannel, 'id'>) => void;
  onUpdateCommunicationChannel: (id: string, channel: Omit<CommunicationChannel, 'id'>) => void;
  onDeleteCommunicationChannel: (id: string) => void;
  onRefreshChannels?: () => Promise<void>;
  automationSettings: AutomationSettings;
  onUpdateAutomationSettings: (settings: AutomationSettings) => void;
  systemPrompt: string;
  /** Backlog V3-1.1 (19-05) : true si le `systemPrompt` est encore le template
   *  par défaut (= pas vraiment configuré par l'user). Synchronise la logique
   *  d'onboarding avec `useSetupWizard` côté gallery. */
  isPositioningTemplate?: boolean;
  // V4-ONBOARDING-CHECKLIST-OPTIONAL — false ⇒ topics/sources optionnels (personal
  // branding sans veille) : ils ne verrouillent plus l'accès aux canaux / posts.
  sectorWatchEnabled?: boolean;
  onUpdateSystemPrompt: (prompt: string) => void;
  newsletterTemplate: string;
  onUpdateNewsletterTemplate: (template: string) => void;
  queuePosts: Post[];
  allPosts?: Post[];
  logoUrl?: string;
  onUpdateLogoUrl?: (url: string) => void;
  user: UserType | null;
  currentSpace: Space | null;
}

export function Sidebar({
  expanded, activeTab, onTabChange, onToggle,
  sources, onAddSource, onUpdateSource, onDeleteSource, onRefreshSources,
  watchTopics, enrichingTopicIds, onAddTopic, onUpdateTopic, onDeleteTopic,
  communicationChannels, onAddCommunicationChannel, onUpdateCommunicationChannel, onDeleteCommunicationChannel, onRefreshChannels,
  automationSettings, onUpdateAutomationSettings,
  systemPrompt, isPositioningTemplate = false, sectorWatchEnabled, onUpdateSystemPrompt,
  newsletterTemplate, onUpdateNewsletterTemplate,
  queuePosts, allPosts, logoUrl, onUpdateLogoUrl,
  user, currentSpace
}: SidebarProps) {

  const [internalSelectedItem, setInternalSelectedItem] = useState<string | null>(null);
  const selectedItem = activeTab !== undefined ? activeTab : internalSelectedItem;
  const { t } = useLanguage();
  const { isReadOnly, isGuest } = useAuth();
  const isAgencyOwner = useIsAgencyOwner();
  // Onboarding séquentiel : verrouille les onglets tant que l'étape précédente n'est pas
  // complétée. Bypassé pour les utilisateurs en lecture seule (cf useOnboardingProgress).
  // Backlog V3-1.1 — passer isPositioningTemplate pour aligner la sémantique
  // "positionnement défini" entre la Sidebar et le SetupWizard (sinon Sidebar
  // considère le template par défaut comme un positionnement valide, ce qui
  // débloque les onglets prématurément et désynchronise les deux vues).
  const onboarding = useOnboardingProgress({
    systemPrompt: systemPrompt || '',
    isPositioningTemplate,
    watchTopics,
    sources,
    communicationChannels,
    // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — débloque Analytics / Engagement
    // uniquement quand au moins 1 post existe (draft inclus).
    posts: allPosts,
    // V4-ONBOARDING-CHECKLIST-OPTIONAL — topics/sources optionnels si veille off.
    sectorWatchEnabled,
  });

  // V4-ZERNIO-INBOX-BADGE 2026-05-28 — poll unreplied count toutes les 60s
  const unrepliedCount = useUnrepliedCount(currentSpace?.id ?? null);

  // V4-ZERNIO-COST-PURGE 2026-06-05 — alerte canal pour le menu Canaux (jaune/rouge)
  const channelAlertLevel = useMemo(
    () => computeChannelAlertLevel(communicationChannels),
    [communicationChannels],
  );

  const handleItemClick = (itemId: string) => {
    // V4-SIDEBAR-UX-NO-TOGGLE 2026-05-28 : si on re-clique sur l'item déjà sélectionné
    // ET expandé, ne PAS fermer (UX peu intuitive — observation user sur Analytics tab).
    // Pour fermer, l'utilisateur passe par le bouton X explicite (handleClose).
    if (expanded && selectedItem === itemId) {
      return;
    }
    if (onTabChange) {
      onTabChange(itemId);
    } else {
      setInternalSelectedItem(itemId);
    }
    if (!expanded) {
      onToggle();
    }
  };

  const handleClose = () => {
    if (onTabChange) {
      onTabChange(null);
    } else {
      setInternalSelectedItem(null);
    }
    if (expanded) {
      onToggle();
    }
  };

  const handleSave = () => {
    Logger.log('Saving changes for:', selectedItem);
    handleClose();
  };

  const renderContent = () => {
    switch (selectedItem) {
      case SIDEBAR_ITEMS.SPACES:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <SpacesTab onClose={handleClose} />
          </Suspense>
        );
      case SIDEBAR_ITEMS.SOURCES:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <SourcesTab
              sources={sources}
              onAddSource={onAddSource}
              onUpdateSource={onUpdateSource}
              onDeleteSource={onDeleteSource}
              onRefreshSources={onRefreshSources}
              onClose={handleClose}
              watchTopics={watchTopics}
              positioningReady={onboarding.unlocked.topics}
            />
          </Suspense>
        );
      case SIDEBAR_ITEMS.WATCH:
        return (
          <WatchTab
            watchTopics={watchTopics}
            enrichingTopicIds={enrichingTopicIds}
            onAddTopic={onAddTopic}
            onUpdateTopic={onUpdateTopic}
            onDeleteTopic={onDeleteTopic}
            onClose={handleClose}
            positioningReady={onboarding.unlocked.topics}
          />
        );
      case SIDEBAR_ITEMS.POSITIONING:
        return (
          <PositioningTab
            systemPrompt={systemPrompt}
            onUpdatePrompt={onUpdateSystemPrompt}
            onClose={handleClose}
          />
        );
      case SIDEBAR_ITEMS.CHANNELS:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <ChannelsTab
              channels={communicationChannels}
              onAddChannel={onAddCommunicationChannel}
              onUpdateChannel={onUpdateCommunicationChannel}
              onDeleteChannel={onDeleteCommunicationChannel}
              onRefreshChannels={onRefreshChannels}
              onClose={handleClose}
              queuePosts={queuePosts}
              allPosts={allPosts}
            />
          </Suspense>
        );
      case SIDEBAR_ITEMS.ANALYTICS:
        return <AnalyticsTab onClose={handleClose} />;
      case SIDEBAR_ITEMS.ENGAGEMENT:
        return <EngagementTab onClose={handleClose} />;
      case SIDEBAR_ITEMS.AUTOMATIONS:
        return (
          <AutomationsTab
            automationSettings={automationSettings}
            communicationChannels={communicationChannels}
            onUpdateSettings={onUpdateAutomationSettings}
            onClose={handleClose}
          />
        );
      case SIDEBAR_ITEMS.NEWSLETTER:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <NewsletterTab
              template={newsletterTemplate}
              onUpdateTemplate={onUpdateNewsletterTemplate}
              onClose={handleClose}
              queuePosts={queuePosts}
              communicationChannels={communicationChannels}
            />
          </Suspense>
        );
      case SIDEBAR_ITEMS.SETTINGS:
        return (
          <SettingsTab
            onClose={handleClose}
          />
        );
      case SIDEBAR_ITEMS.ACCOUNT:
        return <AccountTab onClose={handleClose} />;
      default:
        return null;
    }
  };

  const allMenuItems = getMenuItems(t);
  const spacesMenuItem = isAgencyOwner
    ? allMenuItems.find(item => item.id === SIDEBAR_ITEMS.SPACES)
    : null;
  // ──────────────────────────────────────────────────────────────────────
  // Filtrage / verrouillage des items de menu (2026-05) :
  //   - SPACES → toujours masqué dans la liste principale (rendu séparément si agence owner)
  //   - NEWSLETTER → garde la place mais "Bientôt disponible" (avant : caché complètement)
  //   - AUTOMATIONS → garde la place mais "Bientôt disponible"
  //   - autres items → verrouillage progressif via `onboarding.unlocked.*`
  // Pour les readers d'agence (`isReadOnly`), tout reste accessible en lecture seule —
  // on ne grise rien, on retire juste AUTOMATIONS qui n'a pas de sens en lecture.
  // ──────────────────────────────────────────────────────────────────────
  // V4-SIDEBAR-HIDE-UNTIL-POSITIONING 2026-06-01 — tant que le positionnement n'est
  // pas rempli, on MASQUE (≠ désactive) les items qui en dépendent. La sidebar
  // d'onboarding ne montre que le Positionnement, puis se remplit au fur et à mesure.
  // Une fois `onboarding.unlocked.topics` (= positionnement défini), ces items
  // réapparaissent et reprennent le lock progressif de `getItemLockState`.
  // Les readers d'agence ne sont JAMAIS gatés par l'onboarding (cf useOnboardingProgress).
  const POSITIONING_GATED_ITEMS: string[] = [
    SIDEBAR_ITEMS.WATCH,
    SIDEBAR_ITEMS.SOURCES,
    SIDEBAR_ITEMS.CHANNELS,
    SIDEBAR_ITEMS.ANALYTICS,
    SIDEBAR_ITEMS.ENGAGEMENT,
    SIDEBAR_ITEMS.NEWSLETTER,
  ];
  // Filtrage centralisé (source unique partagée avec le burger mobile, cf navGating.ts).
  // Desktop : Espaces toujours masqué de la nav (showSpacesWhenOwner=false). Le guest
  // ne voit que Engagement (Posts/Analytics/Historique sont ailleurs : zone principale +
  // icônes droite).
  const menuItems = filterNavItems(allMenuItems, {
    isGuest,
    isReadOnly,
    isAgencyOwner: false,
    topicsUnlocked: onboarding.unlocked.topics,
    onboardingReadOnly: onboarding.isReadOnly,
    positioningGatedItems: POSITIONING_GATED_ITEMS,
    showSpacesWhenOwner: false,
  });
  const settingsItem = getSettingsItem(t);

  /**
   * Détermine si un item est verrouillé + le tooltip à afficher.
   *
   * Ordre de priorité :
   *   1. coming-soon (AUTOMATIONS, NEWSLETTER) — même pour readers, on indique "Bientôt"
   *   2. onboarding séquentiel (uniquement si pas readOnly)
   *
   * Note : les readers d'agence ne sont JAMAIS bloqués par l'onboarding (cf useOnboardingProgress).
   */
  const getItemLockState = (itemId: string): { disabled: boolean; tooltip?: string } => {
    // Automatisations & Newsletter restent gelées (non finalisées). Analytics &
    // Engagement réactivées 2026-06-11 (add-on Zernio débloqué) — le lock
    // onboarding « ≥1 post » ci-dessous reprend le relais pour ces deux items.
    if (
      itemId === SIDEBAR_ITEMS.AUTOMATIONS ||
      itemId === SIDEBAR_ITEMS.NEWSLETTER
    ) {
      return {
        disabled: true,
        tooltip: t.onboarding.comingSoonTooltip,
      };
    }
    if (onboarding.isReadOnly) {
      return { disabled: false };
    }
    const onb = t.onboarding;
    // Note (2026-05 chantier P.4) : le lock BRAND_CONFIG a été retiré.
    // La charte graphique est désormais accessible via le toggle de PositioningTab,
    // pas via un item Sidebar dédié.
    if (itemId === SIDEBAR_ITEMS.WATCH && !onboarding.unlocked.topics) {
      return { disabled: true, tooltip: onb.lockedTopicsTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.SOURCES && !onboarding.unlocked.sources) {
      return { disabled: true, tooltip: onb.lockedSourcesTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.CHANNELS && !onboarding.unlocked.channels) {
      return { disabled: true, tooltip: onb.lockedChannelsTooltip };
    }
    // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — Analytics / Engagement nécessitent au
    // moins 1 post existant (sinon page vide / non pertinente)
    if (itemId === SIDEBAR_ITEMS.ANALYTICS && !onboarding.unlocked.analytics) {
      return { disabled: true, tooltip: onb.lockedAnalyticsTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.ENGAGEMENT && !onboarding.unlocked.engagement) {
      return { disabled: true, tooltip: onb.lockedEngagementTooltip };
    }
    return { disabled: false };
  };

  return (
    <>
      {/* Overlay backdrop */}
      {expanded && (
        <div
          className="fixed inset-0 backdrop-blur-sm z-40"
          style={{ backgroundColor: 'rgba(var(--color-background-rgb), 0.8)' }}
          onClick={handleClose}
        />
      )}

      <motion.div
        className="fixed left-0 top-0 h-full shadow-lg z-50"
        style={{
          backgroundColor: 'var(--color-background)',
          borderRight: `1px solid var(--color-border)`,
        }}
        initial={false}
        animate={{ width: expanded ? '50%' : '64px' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className="h-full flex">
          {/* Colonne d'icônes — toujours visible */}
          <div className="flex flex-col w-16 flex-shrink-0">

            {/* Logo */}
            <div className="mb-4 mt-4 flex items-center justify-center h-8">
              <img
                src={logoUrl || DEFAULT_LOGO_URL}
                alt="Logo"
                className="w-8 h-8 object-contain"
              />
            </div>

            {/* Items principaux */}
            {menuItems.map((item, index) => {
              const lock = getItemLockState(item.id);
              // V4-ZERNIO-INBOX-BADGE 2026-05-28 — badge count seulement sur Engagement
              const badge = item.id === SIDEBAR_ITEMS.ENGAGEMENT ? unrepliedCount : 0;
              // V4-ZERNIO-COST-PURGE — alerte canal seulement sur l'item Canaux
              const itemAlert = item.id === SIDEBAR_ITEMS.CHANNELS ? channelAlertLevel : null;
              const itemAlertTooltip = itemAlert === 'danger'
                ? 'Un canal a été déconnecté — reconnectez-le'
                : itemAlert === 'warning'
                ? 'Un canal va être déconnecté sous 7 jours'
                : undefined;
              return (
                <SidebarButton
                  key={item.id}
                  icon={<item.icon className="w-5 h-5" />}
                  label={item.label}
                  isActive={selectedItem === item.id}
                  onClick={() => handleItemClick(item.id)}
                  className={index === 0 ? 'mt-3' : ''}
                  disabled={lock.disabled}
                  disabledTooltip={lock.tooltip}
                  badgeCount={badge}
                  alertLevel={itemAlert}
                  alertTooltip={itemAlertTooltip}
                  testId={`sidebar-item-${item.id}`}
                />
              );
            })}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Gestion des espaces (agency owner uniquement, jamais un guest) */}
            {spacesMenuItem && !isGuest && (
              <SidebarButton
                icon={<spacesMenuItem.icon className="w-5 h-5" />}
                label={spacesMenuItem.label}
                isActive={selectedItem === SIDEBAR_ITEMS.SPACES}
                onClick={() => handleItemClick(SIDEBAR_ITEMS.SPACES)}
                className="mb-0"
                testId="sidebar-item-spaces"
              />
            )}

            {/* Profil utilisateur */}
            {user && (
              <SidebarButton
                icon={
                  user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.email}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5" />
                  )
                }
                label={t.quota.accountTitle}
                isActive={selectedItem === SIDEBAR_ITEMS.ACCOUNT}
                onClick={() => handleItemClick(SIDEBAR_ITEMS.ACCOUNT)}
                title={`${user.email}${currentSpace ? ` (${currentSpace.name})` : ''}`}
                className="mb-0"
                testId="sidebar-item-account"
              />
            )}

            {/* Paramètres (masqué pour un guest lecture seule) */}
            {!isGuest && (
              <SidebarButton
                icon={<settingsItem.icon className="w-5 h-5" />}
                label={settingsItem.label}
                isActive={selectedItem === settingsItem.id}
                onClick={() => handleItemClick(settingsItem.id)}
                className="mb-2"
                testId="sidebar-item-settings"
              />
            )}
          </div>

          {/* Panneau de contenu quand ouvert.
              Fix backlog 1.4 (19-05) : wrapper AnimatePresence + exit pour éviter
              le résidu visuel lors de la fermeture (la transition `width` de l'outer
              motion.div dure 300ms, le panel interne doit s'effacer en harmonie). */}
          <AnimatePresence>
          {expanded && selectedItem && (
            <motion.div
              key="sidebar-content-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.05 }}
              className="flex-1 flex flex-col overflow-hidden backdrop-blur-md"
              style={{
                borderLeft: `1px solid var(--color-border)`,
                backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
              }}
            >
              {/* `data-testid={`tab-panel-${selectedItem}`}` sur les deux branches ci-dessous
                  (V4-FRONT-TAB-PANEL-TESTID 2026-07-30). Un seul point d'ancrage, DÉRIVÉ de
                  l'id d'onglet, donne un sélecteur stable pour les 11 onglets à la fois.

                  Pourquoi c'était nécessaire : `AnalyticsTab`, `EngagementTab`, `NewsletterTab`
                  et `SpacesTab` n'exposaient aucun `data-testid`, et rien n'entourait
                  `renderContent()`. Les tests e2e n'avaient donc que les libellés i18n pour
                  cibler un panneau — fragile sur 3 locales, et surtout impossible à distinguer
                  du même mot rendu ailleurs dans la page (c'est exactement ce qui rendait
                  `10_analytics_engagement` et `11_newsletter` verts sans jamais ouvrir l'onglet).

                  Dérivé, pas écrit en dur : les testids `sidebar-item-spaces` /
                  `sidebar-item-account` plus bas sont, eux, des littéraux qui ont divergé des
                  ids réels (`espaces` / `compte`) — piège à ne pas reproduire ici. */}
              {([
                SIDEBAR_ITEMS.SPACES,
                SIDEBAR_ITEMS.SOURCES,
                SIDEBAR_ITEMS.WATCH,
                SIDEBAR_ITEMS.POSITIONING,
                SIDEBAR_ITEMS.CHANNELS,
                SIDEBAR_ITEMS.AUTOMATIONS,
                SIDEBAR_ITEMS.NEWSLETTER,
                SIDEBAR_ITEMS.ANALYTICS,
                SIDEBAR_ITEMS.ENGAGEMENT,
                SIDEBAR_ITEMS.SETTINGS,
                SIDEBAR_ITEMS.ACCOUNT,
              ] as string[]).includes(selectedItem) ? (
                <div className="flex-1 overflow-hidden" data-testid={`tab-panel-${selectedItem}`}>{renderContent()}</div>
              ) : (
                <>
                  <div
                    className="flex items-center justify-between p-4 flex-shrink-0"
                    style={{ borderBottom: `1px solid var(--color-border)` }}
                  >
                    <h2 style={{ color: 'var(--color-text-primary)' }}>
                      {menuItems.find(item => item.id === selectedItem)?.label || settingsItem.label}
                    </h2>
                    <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-full">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4" data-testid={`tab-panel-${selectedItem}`}>{renderContent()}</div>
                  <div
                    className="p-4 flex-shrink-0"
                    style={{ borderTop: `1px solid var(--color-border)` }}
                  >
                    <Button onClick={handleSave} className="w-full rounded-full">
                      {t.sidebar.saveChanges}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}
